import {
  PaymentMethodSaveRequest,
  PaymentMethodSummaryResponse,
} from '@monetize/types/app';
import { loadStripe } from '@stripe/stripe-js';
import { FunctionComponent as FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { apiPost, handleApiErrorToast } from '../../../api/axios';
import AppLoading from '../../../components/App/AppLoading';
import { MAlert, MBox, MCard, MFlex } from '../../../components/Monetize';
import { PAPER_PAGE_MAX_WIDTH } from '../../../constants/quotes';
import * as ROUTES from '../../../constants/routes';
import { useQuery } from '../../../hooks';
import { tempSharedAccountPaymentMethodState } from '../../../store/global.store';
import { processClientSecretId } from '../../../utils/stripe';

// This component handles the Stripe redirect callback when adding a payment method on Payment Method Share page
export const PaymentMethodShareStripeCallback: FC = () => {
  const params = useParams();
  const tenantId = params.tenantId || '';
  const recordId = params.recordId || '';
  const query = useQuery();
  const navigate = useNavigate();

  const [tempPaymentMethodData, setTempPaymentMethodData] = useAtom(
    tempSharedAccountPaymentMethodState,
  );
  const [isProcessing, setIsProcessing] = useState(true);
  const [savedPaymentMethod, setSavedPaymentMethod] =
    useState<PaymentMethodSummaryResponse>();

  const clientSecretId = query.get('setup_intent_client_secret');
  const token = query.get('token') || '';
  const pgId = query.get('pgId') || '';
  const bgId = query.get('bgId') || '';

  useEffect(() => {
    // Don't run if temp data hasn't loaded yet
    if (!tempPaymentMethodData || !clientSecretId) {
      return;
    }

    const run = async () => {
      try {
        const stripe = await loadStripe(tempPaymentMethodData.stripePublicKey);

        if (!stripe) {
          throw new Error('Sorry, failed to load stripe.');
        }

        const { setupIntent } = await processClientSecretId(
          stripe,
          clientSecretId,
        );

        const paymentToken = setupIntent.id;

        // Save Payment Method to backend
        const paymentMethodSaveRequest: PaymentMethodSaveRequest = {
          paymentGatewayId: tempPaymentMethodData.paymentGatewayId,
          billGroupId: tempPaymentMethodData.billGroupId,
          paymentMethod: {
            amount: 0,
            paymentToken,
          },
        };

        const result = await apiPost<PaymentMethodSummaryResponse>(
          `/api/share/accounts/${recordId}/paymentMethods`,
          paymentMethodSaveRequest,
          {
            params: { token: tempPaymentMethodData.token },
            axiosConfig: {
              excludeUserIdFromHeader: true,
              excludeTenantIdFromHeader: true,
              customXTenantId: tenantId,
            },
          },
        );

        setSavedPaymentMethod(result.data);
        setIsProcessing(false);

        // Clear the temp state
        setTempPaymentMethodData(null);
      } catch (error) {
        handleApiErrorToast(error);
        // On error, redirect back to the payment method share page
        navigate(
          ROUTES.getPaymentMethodShareRoute(
            tenantId,
            recordId,
            token,
            pgId,
            bgId,
          ),
          { replace: true },
        );
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempPaymentMethodData, clientSecretId]);

  if (isProcessing) {
    return <AppLoading loadingText="Processing payment method..." />;
  }

  // Show success message
  return (
    <MBox bg="tGray.shareBack" minH="100vh">
      <MFlex
        width="100%"
        maxW={PAPER_PAGE_MAX_WIDTH}
        flexDirection="column"
        pb="10"
        px="12"
        alignItems="center"
        justifyContent="center"
        justifySelf="center"
        minH="100vh"
      >
        <MCard minW="450px" maxW="450px" variant="borderless">
          <MBox p={6}>
            {savedPaymentMethod && (
              <MAlert
                type="success"
                title="Success"
                width="100%"
                alertProps={{ bgColor: 'unset' }}
              >
                Your payment method has been saved successfully. You can close
                this page.
              </MAlert>
            )}
          </MBox>
        </MCard>
      </MFlex>
    </MBox>
  );
};
